import { Close } from "@mui/icons-material"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ActionReqExecutionModeEnum,
  ActionReqThingTypeMrn,
  ActionReqTypeEnum,
} from "shared/actionReqData"
import { User } from "shared/user"
import { UserThingTypeMrn } from "shared/userData"
import { v4 as uuid } from "uuid"
import { useCreateThingsMutation } from "../features/things/thingsApi"

interface SaUserDetailsDrawerProps {
  open: boolean
  user: User
  onClose: () => void
  onDeleted?: (userId: string) => void
}

const SaUserDetailsDrawer: FC<SaUserDetailsDrawerProps> = ({
  open,
  user,
  onClose,
  onDeleted,
}) => {
  const { t } = useTranslation()
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

  const [createThings] = useCreateThingsMutation()

  const deleteUser = useCallback(async () => {
    try {
      setIsDeleting(true)
      setDeleteError(null)

      const action = {
        thingTypeMrn: ActionReqThingTypeMrn,
        executionMode: ActionReqExecutionModeEnum.sync,
        actionType: ActionReqTypeEnum.DeleteThing,
        idempotencyKey: uuid(),
        tenantId: "noId",
        inputData: {
          id: user.id,
          tenantId: "noId",
          thingTypeMrn: UserThingTypeMrn,
        },
      }

      await createThings({
        body: { things: [action] },
      }).unwrap()

      onDeleted?.(user.id)
      onClose()
    } catch (error: any) {
      setDeleteError(error.message)
    } finally {
      setIsDeleting(false)
    }
  }, [createThings, user.id, onClose, onDeleted])

  return (
    <Drawer data-ui="UI-SL2W-123" anchor="right" open={open} onClose={onClose}>
      <Box p={3} width={300} pt={"75px"}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" gutterBottom>
            {t("userDetails")}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2">{t("firstName")}</Typography>
            <Typography variant="subtitle2" fontFamily={"monospace"}>
              {user.firstName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">{t("lastName")}</Typography>
            <Typography variant="subtitle2" fontFamily={"monospace"}>
              {user.lastName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">{t("email")}</Typography>
            <Typography variant="subtitle2" fontFamily={"monospace"}>
              {user.email}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">{t("status")}</Typography>
            <Typography variant="subtitle2" fontFamily={"monospace"}>
              {user.status}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">{t("externalId")}</Typography>
            <Typography variant="subtitle2" fontFamily={"monospace"}>
              {user.externalId}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">{t("isSuperAdmin")}</Typography>
            <Typography variant="subtitle2" fontFamily={"monospace"}>
              {user.isSuperAdmin ? t("yes") : t("no")}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">{t("createdAt")}</Typography>
            <Typography variant="subtitle2" fontFamily={"monospace"}>
              {user.createdAt || "-"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2">{t("updatedAt")}</Typography>
            <Typography variant="subtitle2" fontFamily={"monospace"}>
              {user.updatedAt || "-"}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Button
          variant="contained"
          color="error"
          onClick={() => setIsConfirmingDelete(true)}
          disabled={isDeleting}
        >
          {t("delete")}
        </Button>
        <Dialog data-ui="UI-SL2W-124"
          open={isConfirmingDelete}
          onClose={() => setIsConfirmingDelete(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("confirmDelete")}</DialogTitle>
          <DialogContent>
            <Stack direction="column" spacing={1}>
              <DialogContentText>
                {t("confirmDeleteUserWarning")}
              </DialogContentText>
              {deleteError && (
                <DialogContentText color={"error"}>
                  {deleteError}
                </DialogContentText>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsConfirmingDelete(false)}
              disabled={isDeleting}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                await deleteUser()
                setIsConfirmingDelete(false)
              }}
              variant="contained"
              color="error"
              disabled={isDeleting}
            >
              {t("delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Drawer>
  )
}

export default SaUserDetailsDrawer
